import { faArrowUpRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { PERMISSION } from 'src/features/auth';
import { CellReservationVersionActions } from 'src/features/reservations/components/CellReservationVersionActions';
import { reservationMessages } from 'src/features/reservations/utils/messages';
import { badgeReservedSpot } from 'src/features/reservations/utils/utils';
import useHasPermissions from 'src/hooks/useHasPermissions';
import {
    CellBadge,
    CellDatetime,
    DataTableColumnGenericHeaderDefinition,
    DataTableColumnHeader,
} from 'src/registry/components/table';
import { Badge } from 'src/registry/ui/Badge';
import { getTooltipCell } from 'src/utils/getTooltipCell';
import { generalPriceMessages } from 'src/utils/messages';

import type { ReservationType } from 'src/features/reservations/types/reservationType';

const columnHelper = createColumnHelper<ReservationType>();

export const ReservationVersionTableColumns = ({
    openDeleteVersionModal,
    openEditVersionModal,
    openCopyVersionModal,
}: {
    openDeleteVersionModal: (version: ReservationType) => void;
    openEditVersionModal: (version: ReservationType) => void;
    openCopyVersionModal: (version: ReservationType) => void;
}) => {
    const { formatMessage, formatNumber } = useIntl();
    const navigate = useNavigate();
    const permissionReservationsSettings = useHasPermissions([PERMISSION.RESERVATIONS_SETTINGS]);

    const reservationVersionActions = permissionReservationsSettings
        ? [
              columnHelper.display({
                  header: DataTableColumnGenericHeaderDefinition,
                  id: 'actions',
                  cell: (props) => (
                      <CellReservationVersionActions
                          deleteCallback={openDeleteVersionModal}
                          editCallback={openEditVersionModal}
                          copyCallback={openCopyVersionModal}
                          {...props}
                      />
                  ),
              }),
          ]
        : [];

    return [
        columnHelper.accessor('version_number', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                const nameToDisplay = row.original.name ? `- ${row.original.name}` : '';
                const today = new Date();
                const { valid_from, valid_to } = row.original;
                const isValidFrom = new Date(valid_from) <= today;
                const isValidTo = valid_to === null || new Date(valid_to) >= today;
                const currentVersion = isValidFrom && isValidTo;
                return (
                    <div>
                        <div className="whitespace-nowrap">{`v${row.original.version_number} ${nameToDisplay}`}</div>
                        {currentVersion && (
                            <Badge className="flex-1 whitespace-nowrap">
                                <div className="size-1.5 rounded-full bg-blue-500"></div>
                                <div>{formatMessage(reservationMessages.currentVersion)}</div>
                            </Badge>
                        )}
                    </div>
                );
            },
            enableHiding: true,
            meta: {
                title: formatMessage(reservationMessages.name),
            },
        }),
        columnHelper.accessor('valid_from', {
            header: DataTableColumnHeader,
            cell: CellDatetime,
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(reservationMessages.reservationValidFrom),
            },
        }),
        columnHelper.accessor('valid_to', {
            header: DataTableColumnHeader,
            cell: CellDatetime,
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(reservationMessages.reservationValidTo),
            },
        }),
        columnHelper.accessor('price', {
            header: DataTableColumnHeader,
            cell: ({ row }) =>
                formatNumber(row.original.price / 100, {
                    style: 'currency',
                    currency: row.original.currency,
                }),
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(generalPriceMessages.price),
            },
        }),
        columnHelper.accessor('price_with_tax', {
            header: DataTableColumnHeader,
            cell: ({ row }) =>
                formatNumber(row.original.price_with_tax / 100, {
                    style: 'currency',
                    currency: row.original.currency,
                }),
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(generalPriceMessages.priceWithVat),
            },
        }),
        columnHelper.accessor('reserve_space', {
            header: DataTableColumnHeader,
            cell: (props) => <CellBadge badgeOptions={badgeReservedSpot} {...props} />,
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(reservationMessages.reservedSpot),
                badgeOptions: badgeReservedSpot,
            },
        }),
        columnHelper.accessor('entry_tolerance_minutes', {
            header: DataTableColumnHeader,
            cell: ({ row }) =>
                `${formatMessage(reservationMessages.minutes, { value: row.original.entry_tolerance_minutes })}`,
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(reservationMessages.entryToleranceTableCol),
            },
        }),
        columnHelper.accessor('exit_tolerance_minutes', {
            header: DataTableColumnHeader,
            cell: ({ row }) =>
                `${formatMessage(reservationMessages.minutes, { value: row.original.exit_tolerance_minutes })}`,
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(reservationMessages.exitToleranceTableCol),
            },
        }),
        columnHelper.accessor('total_reservations_count', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                const url = `/vehicles/reservations?reservation_type=${row.original.reservation_type}&vehicle_stay_reservation_type=${row.original.id}`;
                return (
                    <div
                        className="text-primary-700 flex cursor-pointer items-center font-bold"
                        role="button"
                        tabIndex={0}
                        onClick={() => navigate(url)}
                        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && navigate(url)}
                    >
                        <FontAwesomeIcon icon={faArrowUpRight} className="text-primary-700 mr-2" />
                        <div>{row.original.total_reservations_count}</div>
                    </div>
                );
            },
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(reservationMessages.totalReservations),
            },
        }),
        columnHelper.accessor('description', {
            header: DataTableColumnHeader,
            cell: ({ row }) => getTooltipCell({ value: row.original.description ? row.original.description : '' }),
            enableHiding: true,
            meta: {
                title: formatMessage(reservationMessages.description),
            },
        }),
        ...reservationVersionActions,
    ];
};
